import message from 'antd/lib/message'
import config from '../config'

const NODE_API_URL = config.backend_app

class APIManager {
  getErrorMsg = async (res, showErrorMsg = true) => {
    const error = await res.json()
    const errMsg = error.message ? error.message : error.error
    if (showErrorMsg) {
      message.error(errMsg)
    }
    return errMsg
  }

  getAPICall = async url => {
    try {
      const response = await fetch(`${NODE_API_URL}${url}`, {
        headers: {
          'Content-type': 'application/json',
        },
      })
      return response
    } catch (err) {
      console.error('Get API Call failed', err)
      return { ok: false }
    }
  }

  getAPICallWithCreds = async url => {
    try {
      const response = await fetch(`${NODE_API_URL}${url}`, {
        headers: {
          Authorization: `Bearer ${localStorage.token}`,
          'Content-type': 'application/json',
        },
      })
      return response
    } catch (err) {
      console.error(`DELETE API Call failed: ${err}`)
      return { ok: false }
    }
  }

  deleteAPICallWithCreds = async url => {
    try {
      const response = await fetch(`${NODE_API_URL}${url}`, {
        method: 'delete',
        headers: {
          Authorization: `Bearer ${localStorage.token}`,
          'Content-type': 'application/json',
        },
      })
      return response
    } catch (err) {
      console.error('Get API Call failed', err)
      return { ok: false }
    }
  }

  postAPICallWithCreds = async (url, requestData, method = 'post') => {
    try {
      const response = await fetch(`${NODE_API_URL}${url}`, {
        method: method,
        headers: {
          Authorization: `Bearer ${localStorage.token}`,
          'Content-type': 'application/json',
        },
        body: JSON.stringify(requestData),
      })
      return response
    } catch (err) {
      console.error('Post request with creds failed', err)
      return { ok: false }
    }
  }

  postAPICall = async (url, requestData) => {
    try {
      const response = await fetch(`${NODE_API_URL}${url}`, {
        method: 'post',
        headers: {
          'Content-type': 'application/json',
        },
        body: JSON.stringify(requestData),
      })
      return response
    } catch (err) {
      console.error('Post request failed', err)
      return { ok: false }
    }
  }

  privateApiCall = ({ url, method = 'get', body, query }) => {
    const fetchUrl = new URL(`${NODE_API_URL}${url}`)
    if (query) {
      Object.keys(query).forEach(key => {
        if (typeof query[key] !== 'undefined') {
          fetchUrl.searchParams.append(key, query[key])
        }
      })
    }

    return fetch(fetchUrl, {
      method,
      headers: {
        Authorization: `Bearer ${localStorage.token}`,
        'Content-type': 'application/json',
      },
      body: JSON.stringify(body),
    }).then(res => res.json())
  }

  publicApiCall = ({ url, method = 'get' }) =>
    fetch(`${NODE_API_URL}${url}`, {
      method,
      headers: {
        'Content-type': 'application/json',
      },
    }).then(res => res.json())

  async validateResponse(response, fallbackMessage) {
    if (!response.ok) {
      let error = null
      if (response.json) error = await response.json()
      const message = error?.message ?? error?.error ?? fallbackMessage
      throw new Error(message)
    }
  }

  signup = async ({ data, url }) => {
    const response = await this.postAPICall(url, data)
    if (!response.ok) {
      const err = await this.getErrorMsg(response)
      throw new Error(err)
    }
    const user = await response.json()
    return user
  }

  signupUser = data => this.signup({ url: '/auth/signup', data })

  signupHostUser = data => this.signup({ url: '/auth/signup/host', data })

  getStripeID = async () => {
    try {
      const response = await this.postAPICallWithCreds('/payments/stripe/get')
      if (!response.ok) {
        const error = await response.json()
        const message = error.message ? error.message : error.error
        console.error(`Unable to get Stripe ID: ${message}`)
        throw new Error(message)
      }
      return await response.json()
    } catch (e) {
      throw new Error(e)
    }
  }

  updateUserSettings = async data => {
    try {
      const response = await this.postAPICallWithCreds('/users/me', data, 'put')
      if (!response.ok) {
        const error = await response.json()
        const message = error.message ? error.message : error.error
        throw new Error(message)
      }
      return await response.json()
    } catch (e) {
      throw new Error(e)
    }
  }

  login = async data => {
    const response = await this.postAPICall('/auth/login', data)
    if (!response.ok) {
      console.error('Unable to login')
      const err = await this.getErrorMsg(response)
      throw new Error(err)
    }
    return await response.json()
  }

  getUserProfileData = id => this.publicApiCall({ url: `/users/${id}` })

  checkUsernameExist = username => this.publicApiCall({ url: `/users/${username}/exists` })

  // eslint-disable-next-line consistent-return
  verifyPhoneNumber = async phoneNumber => {
    const response = await this.postAPICall(`/verify-phone`, {
      phone: phoneNumber,
    })
    if (!response.ok) {
      const error = await response.json()
      const message = error.message ? error.message : error.error
      console.error(`Unable to verify Phone Number: ${message}`)
      throw new Error(message)
    }
    return await response.json()
  }
  // eslint-disable-next-line consistent-return

  authPhone = async (phoneNumber, code) => {
    const response = await this.postAPICall('/auth/phone', {
      phone: phoneNumber,
      code,
    })
    if (!response.ok) {
      const error = await response.json()
      const message = error.message ? error.message : error.error
      throw new Error(message)
    }
    return await response.json()
  }

  deleteMatch = async userId => {
    const response = await this.deleteAPICallWithCreds(`/users/me/matches/${userId}`)

    if (response.status !== 200) {
      console.error('Server issue: Unable to fulfill request to delete match')
      return { status: 'not ok' }
    }

    return { status: 'ok' }
  }

  sendResetPasswordEmail = async data => {
    const response = await this.postAPICall('/auth/reset-password/email', data)
    if (!response.ok) {
      const err = await this.getErrorMsg(response)
      throw new Error(err)
    }
    return await response.json()
  }

  resetPassword = async data => {
    const response = await this.postAPICall('/auth/reset-password', data)
    if (!response.ok) {
      const err = await this.getErrorMsg(response)
      throw new Error(err)
    }
    return await response.json()
  }

  updateHostProfileData = async data => {
    try {
      const response = await this.postAPICallWithCreds('/hosts/me', data, 'put')
      if (!response.ok) {
        const error = await response.json()
        const errMsg = error.message ?? error.error
        throw new Error(errMsg)
      }
      return await response.json()
    } catch (e) {
      throw new Error(e)
    }
  }

  getDashboardEvents = async () => {
    const response = await this.getAPICallWithCreds('/events/dashboard')
    if (!response.ok) {
      console.error('Server issue: Unable to fulfill request to get dashboard events')
      throw new Error('Unable to fulfill request to get dashboard events')
    }
    return await response.json()
  }

  getDashboardPastEvents = async () => {
    const response = await this.getAPICallWithCreds('/events/dashboard/past')
    if (!response.ok) {
      console.error('Server issue: Unable to fulfill request to get dashboard past events')
      throw new Error('Unable to fulfill request to get dashboard past events')
    }
    return await response.json()
  }

  getDiscoverEvents = () => this.privateApiCall({ url: '/events/discover', method: 'post' })

  generateSchedule = id => this.privateApiCall({ url: `/events/${id}/schedule`, method: 'post' })

  deleteSchedule = id => this.privateApiCall({ url: `/events/${id}/schedule`, method: 'delete' })

  getEventMatches = id => this.privateApiCall({ url: `/events/${id}/matches` })

  processDateApplication = body =>
    this.privateApiCall({
      url: `/users/application`,
      method: 'PUT',
      body,
    })

  deleteEvent = async ({ id }) => {
    const response = await this.deleteAPICallWithCreds(`/event/${id}`)
    if (!response.ok) {
      throw new Error('Could not delete event')
    }
    return response
  }

  deleteAccount = async () => {
    const response = await this.deleteAPICallWithCreds('/users/me')
    if (!response.ok) {
      const err = await this.getErrorMsg(response)
      throw new Error(err)
    }
    return response
  }

  getPartnerPortalEvents = async () => {
    const response = await this.getAPICallWithCreds(`/events/portal`)
    if (!response.ok) {
      console.error('Server issue: Unable to get all events over from this host')
      const err = await this.getErrorMsg(response)
      throw new Error(err)
    }
    return await response.json()
  }

  getHostCreatedEvents = userId => this.privateApiCall({ url: `/hosts/${userId}/events` })

  getBacheloretteEvents = userId =>
    this.privateApiCall({ url: `/users/${userId}/bachelorette/events` })

  signupForEvent = async ({ eventId, firstPromoterId }) => {
    const response = await this.postAPICallWithCreds(
      `/events/${eventId}/signup`,
      firstPromoterId ? { firstPromoterId } : {} // Adblock blocks firstPromoter so sometimes does not have
    )

    if (!response.ok) {
      const error = await response.json()
      const message = error.message ? error.message : error.error
      throw new Error(message)
    }

    return await response.json()
  }

  deleteSignup = async ({ userId, eventId, refund }) =>
    this.privateApiCall({
      url: `/events/${eventId}/users/${userId}`,
      method: 'delete',
      body: { refund },
    })

  getEvent = id => this.privateApiCall({ url: `/events/${id}` })

  getMe = () => this.privateApiCall({ url: `/users/me` })

  markEventAsSeen = async id => {
    const response = await this.postAPICallWithCreds(`/events/${id}/seen`)
    if (!response.ok) {
      console.error('Could not mark event as seen')
      return
    }
    return await response.json()
  }

  getPartnerPortalEventPageData = async id => {
    const response = await this.getAPICallWithCreds(`/hosts/event/${id}`)
    if (!response.ok) {
      const err = await this.getErrorMsg(response)
      console.error(err)
      throw new Error(err)
    }
    return await response.json()
  }

  getEventPublicInfo = id => this.publicApiCall({ url: `/events/${id}/public` })

  uploadEventImage = async ({ imageData, verticalImageData, ...eventData }) => {
    // it is possible for admin users to create events without image
    eventData.image = imageData ? (await this.uploadImage(imageData)).media.id : undefined
    eventData.verticalImage = verticalImageData
      ? (await this.uploadImage(verticalImageData)).media.id
      : undefined

    return eventData
  }

  createEvent = async data => {
    const eventData = await this.uploadEventImage(data)
    const response = await this.postAPICallWithCreds('/events', eventData)

    await this.validateResponse(
      response,
      'Something went wrong when creating the event! Try again later.'
    )

    return await response.json()
  }

  updateEvent = async data => {
    const eventData = data.imageData ? data : await this.uploadEventImage(data)
    const response = await this.postAPICallWithCreds(`/events/${data.id}`, eventData, 'put')

    if (!response.ok) {
      const error = await response.json()
      const message = error.message ? error.message : error.error
      console.error(`Could not update event: ${message}`)
      throw new Error(message)
    }

    return await response.json()
  }

  inviteUserToEvent = async ({ eventId }) => {
    const res = await this.postAPICallWithCreds(`/events/${eventId}/invite`)

    if (!res.ok) {
      console.error('Could not invite user to event')
      const err = await this.getErrorMsg(res, false)
      throw new Error(err)
    }
  }

  updateStripeId = async authCode => {
    const response = await this.postAPICallWithCreds('/payments/stripe/update', { authCode })
    if (!response.ok) {
      console.error('Server issue: Unable to update Stripe ID')
      await this.getErrorMsg(response)
    }
  }

  getMagicToken = async () => {
    const response = await this.getAPICallWithCreds('/auth/magic-token')
    const res = await response.json()
    return res
  }

  emailInstantSigninLink = async () => {
    const response = await this.postAPICallWithCreds('/auth/magic-token/email')
    if (!response.ok) {
      console.error('Unable to email sign-in link')
      const err = await this.getErrorMsg(response)
      throw new Error(err)
    }
  }

  textIOSLink = async () => {
    const response = await this.postAPICallWithCreds('/download-link/ios/sms')
    if (!response.ok) {
      console.error('Unable to send ios link')
      const err = await this.getErrorMsg(response)
      throw new Error(err)
    }
  }

  getEventMiscellaneous = async () => {
    const response = await this.getAPICallWithCreds('/events/miscellaneous')
    if (!response.ok) {
      console.error('Server issue: Unable to get event miscellaneous')
      const err = await this.getErrorMsg(response)
      throw new Error(err)
    }
    return response.json()
  }

  uploadImage = async image => {
    const response = await this.postAPICallWithCreds('/images', { image })
    await this.validateResponse(
      response,
      'Something went wrong uploading the image! Try again later.'
    )

    return response.json()
  }

  addUserToEvent = async (eventId, userId) => {
    const response = await this.postAPICallWithCreds(
      `/events/${eventId}/signup/${userId}`,
      {},
      'put'
    )
    if (!response.ok) {
      console.error('Unable to add user to event')
      const err = await this.getErrorMsg(response)
      throw new Error(err)
    }
  }

  getStreamingToken = async room => {
    const response = await this.postAPICallWithCreds('/users/streaming/token', { room })

    if (!response.ok) {
      const error = await response.json()
      const message = error.message ? error.message : error.error
      console.error(`Could not get spectate token: ${message}`)
      throw new Error(message)
    }

    return await response.json()
  }

  getUserPublicProfile = async id =>
    this.getAPICallWithCreds(`/users/${id}/profile`).then(parseJSON)

  getForYouProfiles = query => this.privateApiCall({ url: '/for-you/profiles', query })
  getAdminForYouProfiles = () => this.privateApiCall({ url: '/for-you/admin/profiles' })

  likeProfile = async (user, data) => {
    const response = await this.postAPICallWithCreds(`/for-you/profiles/${user}/like`, data, 'put')

    if (!response.ok) {
      const error = await response.json()
      const message = error.message ? error.message : error.error
      console.error(`Could not like profile: ${message}`)
      throw new Error(message)
    }

    return await response.json()
  }

  adminHideProfile = async user => {
    const response = await this.postAPICallWithCreds(
      `/for-you/admin/profiles/${user}`,
      undefined,
      'delete'
    )

    if (!response.ok) {
      const error = await response.json()
      const message = error.message ? error.message : error.error
      console.error(`Could not hide profile: ${message}`)
      throw new Error(message)
    }

    return await response.json()
  }

  getProfileLikes = () => this.privateApiCall({ url: '/for-you' })
  getLikedProfiles = () => this.privateApiCall({ url: '/for-you/profiles/liked' })

  resetProfiles = async () => {
    const response = await this.postAPICallWithCreds('/for-you/profiles/reset')

    if (!response.ok) {
      const error = await response.json()
      const message = error.message ? error.message : error.error
      console.error(`Could not reset profiles: ${message}`)
      throw new Error(message)
    }

    return await response.json()
  }

  getUserWaitlist = async () => {
    const waitlist = await this.privateApiCall({ url: '/waitlist/me' })
    return waitlist?.data
  }

  getAdminWaitlist = query => {
    Object.keys(query).forEach(
      key => (query[key] === undefined || query[key] === '') && delete query[key]
    )

    return this.privateApiCall({
      url: '/waitlist',
      query: query,
    })
  }

  updateWaitlistStatus = async ({ user, data }) => {
    const response = await this.postAPICallWithCreds(`/waitlist/${user}`, data, 'put')

    if (!response.ok) {
      const error = await response.json()
      const message = error.message ? error.message : error.error
      console.error(`Could update waitlist status: ${message}`)
      throw new Error(message)
    }

    return await response.json()
  }

  getHostProfile = id => this.getAPICallWithCreds(`/hosts/${id}`).then(parseJSON)

  getHostDateApplication = async id => this.getAPICallWithCreds(`/hosts/${id}/date`).then(parseJSON)

  submitApplication = async host => {
    const response = await this.postAPICallWithCreds(`/hosts/${host}/date`)

    if (!response.ok) {
      const error = await response.json()
      const message = error.message ? error.message : error.error
      console.error(`Could not submit date application: ${message}`)
      throw new Error(message)
    }

    return await response.json()
  }

  addWallet = async wallet => {
    const response = await this.postAPICallWithCreds('/wallets', wallet)

    if (!response.ok) {
      const error = await response.json()
      const message = error.message ? error.message : error.error
      console.error(`Could not add wallet: ${message}`)
      throw new Error(message)
    }

    return await response.json()
  }

  deleteWallet = async id => {
    const response = await this.postAPICallWithCreds(`/wallets/${id}`, undefined, 'delete')

    if (!response.ok) {
      const error = await response.json()
      const message = error.message ? error.message : error.error
      console.error(`Could not delete wallet: ${message}`)
      throw new Error(message)
    }

    return await response.json()
  }

  getUserNFTs = id => this.getAPICallWithCreds(`/users/${id}/nfts`).then(parseJSON)

  getAvailableRewards = () => this.getAPICallWithCreds('/points/rewards/available').then(parseJSON)

  redeemReward = async data => {
    const response = await this.postAPICallWithCreds('/points/rewards/redeem', data)

    if (!response.ok) {
      const error = await response.json()
      const message = error.message ? error.message : error.error
      console.error(`Could not redeem reward: ${message}`)
      throw new Error(message)
    }

    return await response.json()
  }

  getCompatibilityScore = id =>
    this.getAPICallWithCreds(`/for-you/compatibility/${id}`).then(parseJSON)

  initSocialAuthentication = async ({ platform }) => {
    const response = await this.postAPICallWithCreds(`/accounts/social/${platform}/init`)

    if (!response.ok) {
      const error = await response.json()
      const message = error.message ? error.message : error.error
      throw new Error(message)
    }

    return await response.json()
  }

  verifySocialAccount = async data => {
    const response = await this.postAPICallWithCreds(`/accounts/social`, data)

    if (!response.ok) {
      const error = await response.json()
      const message = error.message ? error.message : error.error
      throw new Error(message)
    }

    return await response.json()
  }

  removeSocialAccount = async platform => {
    const response = await this.deleteAPICallWithCreds(`/accounts/social/${platform}`)

    if (response.status !== 200) {
      return { status: 'not ok' }
    }

    return { status: 'ok' }
  }

  reverseGeocodeLocation = async data => {
    const response = await this.postAPICallWithCreds(`/utils/location/reverse-geocode`, data)

    if (!response.ok) {
      const error = await response.json()
      const message = error.message ? error.message : error.error
      throw new Error(message)
    }

    return await response.json()
  }

  getAiModels = async provider => {
    const models = await this.getAPICallWithCreds(`/ai/models?provider=${provider}`).then(parseJSON)
    return models?.data
  }

  getApplicationStatus = async () => {
    const models = await this.getAPICallWithCreds('/application-status/me').then(parseJSON)
    return models?.data
  }

  getMembershipPlans = async () => {
    const response = await this.getAPICallWithCreds('/memberships/plans').then(parseJSON)
    return response?.data
  }

  generateDreamBoothImage = async data => {
    const response = await this.postAPICallWithCreds(`/ai/image/dream-booth`, data)

    if (!response.ok) {
      const error = await response.json()
      const message = error.message ? error.message : error.error
      throw new Error(message)
    }

    return await response.json()
  }

  generateDreamBoothModel = async data => {
    const response = await this.postAPICallWithCreds(`/ai/models/dream-booth`, data)

    if (!response.ok) {
      const error = await response.json()
      const message = error.message ? error.message : error.error
      throw new Error(message)
    }

    return await response.json()
  }

  subscribeToMembership = async data => {
    const response = await this.postAPICallWithCreds(`/memberships`, data)

    if (!response.ok) {
      const error = await response.json()
      const message = error.message ? error.message : error.error
      throw new Error(message)
    }

    return await response.json()
  }

  updateInviteReferralCode = async ({ referralCode }) => {
    const response = await this.postAPICallWithCreds(
      `/users/invite/${referralCode}`,
      undefined,
      'PATCH'
    )

    if (!response.ok) {
      const error = await response.json()
      const message = error.message ? error.message : error.error
      throw new Error(message)
    }

    return await response.json()
  }

  updateMembershipStatus = async data =>
    this.handlePostRequest({
      url: '/users/me/membership-status',
      data,
      method: 'PATCH',
    })

  getReferrer = async () => {
    const response = await this.getAPICallWithCreds('/users/me/referrer').then(parseJSON)
    return response?.data
  }

  adminGetUserProfile = async id => {
    const response = await this.getAPICallWithCreds(`/v1/admin/users/${id}`).then(parseJSON)
    return response?.data
  }

  addEventGalleryImage = async ({ eventId, data }) => {
    const response = await this.postAPICallWithCreds(`/events/${eventId}/gallery`, data)

    if (!response.ok) {
      const error = await response.json()
      const message = error.message ? error.message : error.error
      throw new Error(message)
    }

    return await response.json()
  }

  adminGetActiveUsers = async (query = {}) => {
    const response = await this.privateApiCall({ url: '/v1/admin/users', query })
    return response?.data
  }

  adminGetMatches = async () => {
    const response = await this.getAPICallWithCreds('/v1/admin/matches').then(parseJSON)
    return response?.data
  }

  adminGetRecommendations = async (query = {}) => {
    const response = await this.privateApiCall({ url: '/v1/admin/matches/recommendations', query })
    return response?.data
  }

  adminGetMatchFeedback = async (query = {}) => {
    const response = await this.privateApiCall({ url: '/v1/admin/match-feedback', query })
    return response?.data
  }

  adminDeleteUser = async id =>
    this.handlePostRequest({
      url: `/v1/admin/users/${id}`,
      method: 'delete',
    })

  adminAddFreeSubscription = async ({ data }) => {
    const response = await this.postAPICallWithCreds('/v1/admin/memberships/trial', data)

    if (!response.ok) {
      const error = await response.json()
      const message = error.message ? error.message : error.error
      throw new Error(message)
    }

    return await response.json()
  }

  adminPreventSubscriptionRenewal = async ({ data }) => {
    const response = await this.postAPICallWithCreds(
      '/v1/admin/memberships/renewal',
      data,
      'delete'
    )

    if (!response.ok) {
      const error = await response.json()
      const message = error.message ? error.message : error.error
      throw new Error(message)
    }

    return await response.json()
  }

  adminPauseSubscription = async userId =>
    this.handlePostRequest({
      url: `/v1/admin/memberships/users/${userId}/pause`,
    })

  adminResumeSubscription = async userId =>
    this.handlePostRequest({
      url: `/v1/admin/memberships/users/${userId}/resume`,
    })

  adminGetAICompatibilityProfiles = async ({ userId, data = {} }) => {
    return this.handlePostRequest({
      url: `/v1/admin/ai/users/${userId}/profiles`,
      data,
    })
  }

  adminDownloadAnalytics = async ({ path, data }) =>
    this.handlePostRequest({
      url: `/v1/admin/analytics/downloads/${path}`,
      data,
      blob: true,
    })

  adminCreateMatch = async data =>
    this.handlePostRequest({
      url: '/v1/admin/matches/recommendations',
      data,
    })

  adminCreateMatchFeedback = async data =>
    this.handlePostRequest({
      url: '/v1/admin/match-feedback',
      data,
    })

  adminUpdateUser = async ({ id, data }) =>
    this.handlePostRequest({
      url: `/v1/admin/users/${id}`,
      data,
      method: 'PATCH',
    })

  async handlePostRequest({ url, data, blob, method = 'post' }) {
    const response = await this.postAPICallWithCreds(url, data, method)
    if (!response.ok) {
      if (!response.json) {
        throw new Error('Unable to process request')
      }

      const error = await response.json()
      const message = error.message ? error.message : error.error
      throw new Error(message)
    }

    if (blob) {
      return response.blob()
    }

    const responseJson = await response.json()
    return responseJson?.data || responseJson
  }
}

const parseJSON = res => res.ok && res.json()

const apiMgr = new APIManager()

export default apiMgr
